import { formatNumber } from "./format.ts";
import { ChartFrame, strokeClass } from "./frame.tsx";
import { Legend } from "./Legend.tsx";
import { type ChartSeries, resolveSeries, type SeriesDatum } from "./series.ts";

export interface LineChartProps {
  title: string;
  summary: string;
  series: readonly ChartSeries[];
  data: readonly SeriesDatum[];
  formatValue?: (value: number) => string;
  formatLabel?: (label: string) => string;
  labelHeader: string;
  className?: string;
}

/** Dash patterns per series position: solid, dashed, dotted, dash-dot, long dash. */
const DASHES = ["", "6 4", "1.5 3", "8 3 1.5 3", "12 4"] as const;
const identity = (label: string) => label;

/**
 * Lines over time. Series differ by dash pattern as well as colour, and the legend gives each
 * series' latest value.
 */
export function LineChart({
  title,
  summary,
  series,
  data,
  formatValue = formatNumber,
  formatLabel = identity,
  labelHeader,
  className,
}: LineChartProps) {
  const resolved = resolveSeries(series);
  const dashes = resolved.map((_, i) => DASHES[i % DASHES.length] ?? "");
  const max = Math.max(0, ...data.flatMap((d) => resolved.map((s) => d.values[s.key] ?? 0)));
  const width = 100;
  const x = (i: number) => (data.length > 1 ? (i / (data.length - 1)) * width : width / 2);
  const y = (v: number) => (max > 0 ? 100 - (v / max) * 96 - 2 : 98);
  const lastValues = Object.fromEntries(
    resolved.map((s) => [s.key, formatValue(data.at(-1)?.values[s.key] ?? 0)]),
  );
  const table = {
    columns: [labelHeader, ...resolved.map((s) => s.label)],
    rows: data.map((d) => [
      formatLabel(d.label),
      ...resolved.map((s) => formatValue(d.values[s.key] ?? 0)),
    ]),
  };
  const first = data[0];
  const last = data.at(-1);
  return (
    <ChartFrame title={title} summary={summary} table={table} className={className}>
      {({ summaryId }) => (
        <div className="flex flex-col gap-2">
          <Legend series={resolved} values={lastValues} dashes={dashes} />
          <div className="flex items-stretch gap-2">
            <div className="flex flex-col justify-between text-right text-xs tabular-nums text-chart-label">
              <span>{formatValue(max)}</span>
              <span>{formatValue(0)}</span>
            </div>
            <svg
              role="img"
              aria-describedby={summaryId}
              viewBox={`0 0 ${width} 100`}
              preserveAspectRatio="none"
              className="h-32 w-full min-w-0 border-b border-chart-grid"
            >
              <title>{title}</title>
              {resolved.map((s, si) => (
                <polyline
                  key={s.key}
                  fill="none"
                  points={data.map((d, i) => `${x(i)},${y(d.values[s.key] ?? 0)}`).join(" ")}
                  className={strokeClass(s.tone)}
                  strokeWidth={2}
                  strokeDasharray={dashes[si] || undefined}
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                />
              ))}
            </svg>
          </div>
          {first && last ? (
            <div className="flex justify-between pl-8 text-xs text-chart-label" aria-hidden="true">
              <span>{formatLabel(first.label)}</span>
              {last !== first ? <span>{formatLabel(last.label)}</span> : null}
            </div>
          ) : null}
        </div>
      )}
    </ChartFrame>
  );
}
